import { OnDestroy, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, filter, map, publish, share, switchMap, takeUntil, tap } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

export function ofType(...types: string[]) {
    return filter((action: ComponentAction<any>) => types.includes(action.type));
}

export class ComponentAction<T> {
    constructor(public type: string, public data: T) { }
}

@UntilDestroy()
@Injectable()
export class ComponentStore<T extends object> implements OnDestroy {

    private readonly _state$: BehaviorSubject<T> = new BehaviorSubject(null);
    private readonly _actions$ = new Subject<ComponentAction<any>>();

    public get state$(): Observable<T> {
        return this._state$.asObservable();
    }

    public get actions$(): Observable<ComponentAction<any>> {
        return this._actions$;
    }

    public get state(): T {
        return this._state$.getValue();
    }

    protected constructor(initialState: T) {

        this._state$.next(initialState);
        this._actions$
            .pipe(
                untilDestroyed(this),
                share()
            )
            .subscribe();
    }

    ngOnDestroy(): void {
        this._actions$.complete();
        this._state$.complete();
        console.log('Store Destroyed');
    }

    public dispatch(type: string, data: any): Observable<ComponentAction<any>> {
        this._actions$.next(new ComponentAction<any>(type, data));
        return this.actions$;
    }

    public setState(partialState: Partial<T>): void {
        const state = Object.assign({}, this.state);
        const newState = Object.assign(state, partialState);
        this._state$.next(newState);
    }

    protected createSelector<I>(selector: (state: T) => I) {
        return this.state$
            .pipe(
                map(selector),
                distinctUntilChanged()
            ) as Observable<I>;
    }
}

export enum ActionStatus {
    NotStarted,
    InProgress,
    Completed,
    Error
}

